import { resolveTaxonomyHeading } from 'src/utils/resolveTaxonomyHeading';

export interface BuildShopPageTitle {
  /** The matched content entry's heading, when content exists for the page. */
  heading?: string;
  /**
   * Content-authored HTML title override (`seo.title_override`). When set, it is
   * used verbatim as the whole title, taking precedence over everything else.
   */
  pageTitleOverride?: string;
  /**
   * The lowest active taxonomy level's label (e.g. "Earrings"), used when the
   * page has no content heading. Absent at the surface root, where no taxonomy
   * level is active.
   */
  taxonomyLabel?: string;
}

const MAX_PAGE_TITLE_LENGTH_FOR_SEO = 60;

/**
 * Last-resort HTML title for shop PLPs with neither a content heading nor an
 * active taxonomy level (e.g. the surface root). Such pages are not indexable,
 * so the title carries little SEO weight — it only needs to be a sensible
 * browser-tab/bookmark label.
 */
export const SHOP_PAGE_TITLE_FALLBACK = 'Shop | NOTHS';

/**
 * Builds the HTML title for a shop (taxonomy) PLP. A content-authored
 * `pageTitleOverride` wins verbatim. Otherwise, content is optional on this
 * route, so the label falls back to the lowest active taxonomy level, and then
 * to a generic title when no taxonomy level is active.
 */
export const buildShopPageTitle = ({
  heading,
  pageTitleOverride,
  taxonomyLabel,
}: BuildShopPageTitle): string => {
  if (pageTitleOverride) {
    return pageTitleOverride;
  }

  const label = resolveTaxonomyHeading({ heading, taxonomyLabel });

  if (!label) {
    return SHOP_PAGE_TITLE_FALLBACK;
  }

  const pageTitle = `${label} | NOTHS`;

  if (pageTitle.length <= MAX_PAGE_TITLE_LENGTH_FOR_SEO) {
    return pageTitle;
  }

  return label;
};
